(() => {
  /**
   * @typedef {object} ScrollLockState
   * @property {string} rootOverflow
   */

  const SHELL_SELECTOR = '.spx-header-search-shell';
  const INPUT_SELECTOR = '.spx-header-search__input';
  const INTERNAL_RESULTS_SELECTOR = '.spx-header-search__internal-results';
  const EXTERNAL_RESULTS_SELECTOR = '[data-external-predictive-results]';
  const DEV_HASH = '#dev';
  const DEV_QUERY_KEYS = ['spx_search_dev', 'spxsearchdev'];
  const DEBUG_PREFIX = '[spx-header-search-external]';

  /** @type {ScrollLockState | null} */
  let scrollLockState = null;

  /** @param {...unknown} args */
  const debug = (...args) => {
    console.info(DEBUG_PREFIX, ...args);
  };

  const isDevSearchMode = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return DEV_QUERY_KEYS.some((key) => searchParams.get(key) === '1') || window.location.hash === DEV_HASH;
  };

  /**
   * @param {EventTarget | null} target
   */
  const isInteractive = (target) => {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(
      target.closest('button') ||
        target.closest('a') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('textarea')
    );
  };

  const lockScroll = () => {
    if (scrollLockState) {
      debug('lockScroll skipped: already locked');
      return;
    }

    const bodyStyle = document.body.style;
    const rootStyle = document.documentElement.style;
    scrollLockState = {
      rootOverflow: rootStyle.overflow,
    };

    document.documentElement.classList.add('spx-search-scroll-lock');
    rootStyle.setProperty('overflow', 'hidden', 'important');

    debug('lockScroll applied', {
      htmlClass: document.documentElement.classList.contains('spx-search-scroll-lock'),
    });
  };

  const unlockScroll = () => {
    if (!scrollLockState) {
      debug('unlockScroll skipped: not locked');
      return;
    }

    const rootStyle = document.documentElement.style;
    const { rootOverflow } = scrollLockState;
    if (rootOverflow) {
      rootStyle.setProperty('overflow', rootOverflow);
    } else {
      rootStyle.removeProperty('overflow');
    }
    document.documentElement.classList.remove('spx-search-scroll-lock');

    scrollLockState = null;
    debug('unlockScroll applied');
  };

  /**
   * @param {EventTarget | null} target
   */
  const findInputByTarget = (target) => {
    if (!(target instanceof Element)) return null;
    const input = target.closest(INPUT_SELECTOR);
    return input instanceof HTMLInputElement ? input : null;
  };

  /**
   * @param {EventTarget | null} target
   */
  const findShellByTarget = (target) => {
    if (!(target instanceof Element)) return null;
    const shell = target.closest(SHELL_SELECTOR);
    return shell instanceof HTMLElement ? shell : null;
  };

  /**
   * @param {HTMLElement} shell
   * @param {HTMLInputElement} input
   */
  const syncEmptyState = (shell, input) => {
    shell.dataset.empty = input.value.trim() ? 'false' : 'true';
  };

  /**
   * Positions the fixed mega panel directly below the search input.
   * @param {HTMLElement} shell
   * @param {HTMLInputElement} input
   */
  const syncMegaPosition = (shell, input) => {
    const inputBottom = Math.round(input.getBoundingClientRect().bottom);
    shell.style.setProperty('--spx-search-mega-top', `${inputBottom}px`);
  };

  /**
   * Keeps the header menu mutually exclusive with the search mega panel.
   * @param {HTMLElement} shell
   * @param {HTMLInputElement} input
   * @param {boolean} isOpen
   */
  const setSearchOpen = (shell, input, isOpen) => {
    const header = shell.closest('header-component');

    if (isOpen) {
      if (header instanceof HTMLElement) header.dataset.searchOpen = 'true';
      syncMegaPosition(shell, input);
      requestAnimationFrame(() => syncMegaPosition(shell, input));
      return;
    }

    if (header instanceof HTMLElement) header.removeAttribute('data-search-open');
  };

  /**
   * @param {HTMLElement} shell
   */
  const syncExternalResults = (shell) => {
    const internalResults = shell.querySelector(INTERNAL_RESULTS_SELECTOR);
    const externalResults = shell.querySelector(EXTERNAL_RESULTS_SELECTOR);

    if (!(internalResults instanceof HTMLElement) || !(externalResults instanceof HTMLElement)) return;

    externalResults.querySelectorAll('[data-search-results]').forEach((node) => node.remove());

    const internalSearchResults = internalResults.querySelector('[data-search-results]');
    if (!(internalSearchResults instanceof HTMLElement)) return;

    externalResults.appendChild(internalSearchResults.cloneNode(true));
  };

  /**
   * @param {Element} shell
   */
  const setupShell = (shell) => {
    if (!(shell instanceof HTMLElement) || shell.dataset.spxExternalMegaReady === 'true') return;

    const internalResults = shell.querySelector(INTERNAL_RESULTS_SELECTOR);
    const externalResults = shell.querySelector(EXTERNAL_RESULTS_SELECTOR);
    const input = shell.querySelector(INPUT_SELECTOR);

    if (!(internalResults instanceof HTMLElement) || !(externalResults instanceof HTMLElement)) return;

    const observer = new MutationObserver(() => {
      syncExternalResults(shell);
    });

    observer.observe(internalResults, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    externalResults.addEventListener('click', (event) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (isInteractive(event.target)) return;
      input.focus();
    });

    shell.dataset.spxExternalMegaReady = 'true';
    syncExternalResults(shell);
  };

  /** @param {FocusEvent} event */
  const onDocumentFocusIn = (event) => {
    const input = findInputByTarget(event.target);
    if (!input) return;
    const shell = findShellByTarget(input);
    if (!shell || shell.dataset.devOpen === 'true') return;

    syncEmptyState(shell, input);
    setSearchOpen(shell, input, true);
    debug('focusin -> lock', { value: input.value, empty: shell.dataset.empty });
    lockScroll();
  };

  /** @param {Event} event */
  const onDocumentInput = (event) => {
    const input = findInputByTarget(event.target);
    if (!input) return;
    const shell = findShellByTarget(input);
    if (!shell) return;

    syncEmptyState(shell, input);
    setSearchOpen(shell, input, true);
    debug('input -> lock', { value: input.value, empty: shell.dataset.empty });
    lockScroll();
  };

  /** @param {FocusEvent} event */
  const onDocumentFocusOut = (event) => {
    const shell = findShellByTarget(event.target);
    if (!shell || shell.dataset.devOpen === 'true') return;

    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && shell.contains(nextTarget)) return;

    const input = shell.querySelector(INPUT_SELECTOR);
    if (input instanceof HTMLInputElement) setSearchOpen(shell, input, false);
    debug('focusout -> unlock');
    unlockScroll();
  };

  /** @param {KeyboardEvent} event */
  const onDocumentKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const input = findInputByTarget(event.target);
    if (!input) return;
    const shell = findShellByTarget(input);
    if (!shell || shell.dataset.devOpen === 'true') return;

    event.preventDefault();
    setSearchOpen(shell, input, false);
    input.blur();
    unlockScroll();
    debug('escape -> close');
  };

  const openSearchInDevMode = () => {
    const enabled = isDevSearchMode();
    const shells = Array.from(document.querySelectorAll(SHELL_SELECTOR)).filter((el) => el instanceof HTMLElement);
    if (!shells.length) return;

    if (!enabled) {
      shells.forEach((shell) => {
        shell.dataset.devOpen = 'false';
        shell.dataset.empty = 'false';
        const input = shell.querySelector(INPUT_SELECTOR);
        if (input instanceof HTMLInputElement) setSearchOpen(shell, input, false);
      });
      unlockScroll();
      return;
    }

    lockScroll();
    shells.forEach((shell) => {
      shell.dataset.devOpen = 'true';
      shell.dataset.empty = 'true';
      const input = shell.querySelector(INPUT_SELECTOR);
      if (input instanceof HTMLInputElement) setSearchOpen(shell, input, true);
    });

    const firstShell = shells[0];
    if (!(firstShell instanceof HTMLElement)) return;
    const firstInput = firstShell.querySelector(INPUT_SELECTOR);
    if (firstInput instanceof HTMLInputElement) {
      requestAnimationFrame(() => firstInput.focus());
    }
  };

  const syncOpenSearchPositions = () => {
    document.querySelectorAll(SHELL_SELECTOR).forEach((shell) => {
      if (!(shell instanceof HTMLElement)) return;
      if (!shell.matches(':focus-within') && shell.dataset.devOpen !== 'true') return;

      const input = shell.querySelector(INPUT_SELECTOR);
      if (input instanceof HTMLInputElement) syncMegaPosition(shell, input);
    });
  };

  debug('script loaded');
  document.querySelectorAll(SHELL_SELECTOR).forEach(setupShell);
  document.addEventListener('focusin', onDocumentFocusIn);
  document.addEventListener('input', onDocumentInput);
  document.addEventListener('focusout', onDocumentFocusOut);
  document.addEventListener('keydown', onDocumentKeyDown);
  window.addEventListener('hashchange', openSearchInDevMode);
  window.addEventListener('resize', syncOpenSearchPositions);
  requestAnimationFrame(openSearchInDevMode);
})();
